import { cn } from '../lib/cn.js';

export type GaugeTone = 'success' | 'warning' | 'danger';

/**
 * 85 % is the point where a warning still changes the outcome — past 100 % it is
 * only a verdict. Hence: prevent before, judge after.
 */
export function gaugeTone(value: number, target: number): GaugeTone {
  if (target <= 0) return 'success';
  const ratio = value / target;
  if (ratio > 1) return 'danger';
  if (ratio >= 0.85) return 'warning';
  return 'success';
}

const FILL: Record<GaugeTone, string> = {
  success: 'jauge-succes',
  warning: 'jauge-attention',
  danger: 'jauge-erreur',
};

export const TONE_TEXT: Record<GaugeTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-error',
};

interface GaugeProps {
  value: number;
  /** null renders an inert track — no goal is set, so there is nothing to fill. */
  target: number | null;
  /** Spoken by screen readers, e.g. "1240 sur 1800 calories". */
  label: string;
  tone?: GaugeTone;
  className?: string;
}

/**
 * `.jauge` du kit. Fills to 100 % and turns red when over target — never overflows its
 * track. The magnitude of the overshoot is carried by the number next to the gauge.
 */
export function Gauge({ value, target, label, tone, className }: GaugeProps): React.ReactElement {
  const hasTarget = target !== null && target > 0;
  const ratio = hasTarget ? value / target : 0;
  const resolved = tone ?? (hasTarget ? gaugeTone(value, target) : 'success');
  // Keep a sliver visible so a barely-started day does not read as an empty one.
  const pct = ratio <= 0 ? 0 : Math.min(100, Math.max(2, Math.round(ratio * 100)));

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      {...(hasTarget ? { 'aria-valuemax': target } : {})}
      className={cn('jauge', FILL[resolved], !hasTarget && 'opacity-40', className)}
      style={{ '--valeur': hasTarget ? pct : 0 } as React.CSSProperties}
    />
  );
}
